import React, { createContext, useContext, useState, useCallback } from "react"
import styled, { keyframes } from "styled-components"
import { FiInfo, FiCheckCircle, FiAlertTriangle, FiXCircle, FiX } from "react-icons/fi"

const ToastContext = createContext(null)

export function useToast() {
    return useContext(ToastContext)
}

/* ==== Animación y estilos ==== */
const slideIn = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

export const ToastContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 9999;
`

export const ToastBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  animation: ${slideIn} 0.25s ease forwards;
  min-width: 260px;
  max-width: 380px;
  color: #1e293b;
  background: ${({ type }) =>
        type === "success"
            ? "#ecfdf5"
            : type === "error"
                ? "#fef2f2"
                : type === "warning"
                    ? "#fffbeb"
                    : "#eff6ff"};
  border: 1px solid
    ${({ type }) =>
        type === "success"
            ? "#a7f3d0"
            : type === "error"
                ? "#fecaca"
                : type === "warning"
                    ? "#fde68a"
                    : "#bfdbfe"};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
`

export const IconWrapper = styled.div`
  font-size: 18px;
  color: ${({ type }) =>
        type === "success"
            ? "#059669"
            : type === "error"
                ? "#dc2626"
                : type === "warning"
                    ? "#d97706"
                    : "#2563eb"};
  flex-shrink: 0;
`

export const CloseBtn = styled.button`
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  color: #64748b;
  transition: color 0.2s ease;
  &:hover { color: #334155; }
`

/* ==== Toast individual ==== */
function Toast({ id, type = "info", message, onClose }) {
    const getIcon = () => {
        switch (type) {
            case "success": return <FiCheckCircle />
            case "error": return <FiXCircle />
            case "warning": return <FiAlertTriangle />
            default: return <FiInfo />
        }
    }

    React.useEffect(() => {
        const timer = setTimeout(() => onClose(id), 4000)
        return () => clearTimeout(timer)
    }, [id, onClose])

    return (
        <ToastBox type={type}>
            <IconWrapper type={type}>{getIcon()}</IconWrapper>
            <div>{message}</div>
            <CloseBtn onClick={() => onClose(id)}>
                <FiX />
            </CloseBtn>
        </ToastBox>
    )
}

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([])

    const addToast = useCallback((message, type = "info") => {
        const id = Math.random().toString(36).slice(2)
        setToasts(prev => [...prev, { id, message, type }])
    }, [])

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id))
    }, [])

    // Métodos específicos por tipo
    const toast = {
        success: (msg) => addToast(msg, "success"),
        error: (msg) => addToast(msg, "error"),
        warning: (msg) => addToast(msg, "warning"),
        info: (msg) => addToast(msg, "info"),
    }

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer>
                {toasts.map(t => (
                    <Toast key={t.id} {...t} onClose={removeToast} />
                ))}
            </ToastContainer>
        </ToastContext.Provider>
    )
}